import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/imageUtils';
import { FiPlus, FiEdit, FiTrash2, FiRefreshCw, FiSave, FiX, FiUpload, FiImage } from 'react-icons/fi';
import toast from 'react-hot-toast';

const logoPublicUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return getImageUrl(url);
};

const DadosEmpresa = () => {
  const [dadosEmpresa, setDadosEmpresa] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [dadosEdit, setDadosEdit] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    endereco: '',
    bairro: '',
    cidade: '',
    telefone: '',
    email: '',
    site: '',
    redes_sociais: '',
    descricao: ''
  });

  useEffect(() => {
    fetchDadosEmpresa();
  }, []);

  const fetchDadosEmpresa = async () => {
    try {
      const response = await api.get('/api/dados-empresa');
      setDadosEmpresa(response.data);
    } catch (error) {
      console.error('Erro ao carregar dados da empresa:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar dados da empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (dados = null) => {
    if (dados) {
      setDadosEdit(dados);
      setFormData({
        nome: dados.nome || '',
        cnpj: dados.cnpj || '',
        endereco: dados.endereco || '',
        bairro: dados.bairro || '',
        cidade: dados.cidade || '',
        telefone: dados.telefone || '',
        email: dados.email || '',
        site: dados.site || '',
        redes_sociais: dados.redes_sociais || '',
        descricao: dados.descricao || ''
      });
      // Se já tem logo, mostrar preview
      if (dados.logo_url) {
        setLogoPreview(logoPublicUrl(dados.logo_url));
      } else {
        setLogoPreview(null);
      }
    } else {
      setDadosEdit(null);
      setFormData({
        nome: '',
        cnpj: '',
        endereco: '',
        bairro: '',
        cidade: '',
        telefone: '',
        email: '',
        site: '',
        redes_sociais: '',
        descricao: ''
      });
      setLogoPreview(null);
    }
    setLogoFile(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDadosEdit(null);
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Apenas imagens são permitidas (jpeg, jpg, png, gif, webp)');
        return;
      }
      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A imagem deve ter no máximo 5MB');
        return;
      }
      setLogoFile(file);
      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      
      // Adicionar campos de texto
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });
      
      // Adicionar arquivo de logo se houver
      if (logoFile) {
        submitData.append('logo', logoFile);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      if (dadosEdit) {
        await api.put(`/api/dados-empresa/${dadosEdit.id}`, submitData, config);
        toast.success('Dados da empresa atualizados com sucesso!');
      } else {
        await api.post('/api/dados-empresa', submitData, config);
        toast.success('Dados da empresa criados com sucesso!');
      }
      fetchDadosEmpresa();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar dados da empresa');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir estes dados da empresa?')) {
      return;
    }

    try {
      await api.delete(`/api/dados-empresa/${id}`);
      toast.success('Dados da empresa excluídos com sucesso!');
      fetchDadosEmpresa();
    } catch (error) {
      console.error('Erro ao excluir dados da empresa:', error);
      toast.error(error.response?.data?.error || 'Erro ao excluir dados da empresa');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Informações da Empresa</h1>
          <p className="text-white/70 mt-1">Gerencie os dados cadastrais da NetalimaTurismo</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchDadosEmpresa}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
          >
            <FiRefreshCw size={18} />
            Atualizar
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus size={18} />
            Novo Registro
          </button>
        </div>
      </div>

      {/* Lista de Dados */}
      <div className="bg-sky-50 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Logo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CNPJ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  E-mail
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dadosEmpresa.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Nenhum registro encontrado
                  </td>
                </tr>
              ) : (
                dadosEmpresa.map((dados) => (
                  <tr key={dados.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {dados.logo_url ? (
                        <img
                          src={logoPublicUrl(dados.logo_url)}
                          alt="Logo"
                          className="h-12 w-12 object-contain rounded border border-gray-200 bg-white p-1"
                        />
                      ) : (
                        <div className="h-12 w-12 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                          <FiImage size={20} className="text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{dados.nome}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{dados.cnpj || 'Não informado'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{dados.telefone || 'Não informado'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{dados.email || 'Não informado'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenModal(dados)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Editar dados"
                        >
                          <FiEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(dados.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Excluir dados"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {dadosEdit ? 'Editar Dados da Empresa' : 'Novos Dados da Empresa'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome da Empresa *
                    </label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CNPJ
                    </label>
                    <input
                      type="text"
                      value={formData.cnpj}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        const formatted = value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
                        setFormData({ ...formData, cnpj: formatted });
                      }}
                      maxLength="18"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Endereço
                    </label>
                    <input
                      type="text"
                      value={formData.endereco}
                      onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bairro
                    </label>
                    <input
                      type="text"
                      value={formData.bairro}
                      onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cidade
                    </label>
                    <input
                      type="text"
                      value={formData.cidade}
                      onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={formData.telefone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        const formatted = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
                        setFormData({ ...formData, telefone: formatted });
                      }}
                      maxLength="15"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Site
                    </label>
                    <input
                      type="url"
                      value={formData.site}
                      onChange={(e) => setFormData({ ...formData, site: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      placeholder="https://www.exemplo.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Redes Sociais
                    </label>
                    <input
                      type="text"
                      value={formData.redes_sociais}
                      onChange={(e) => setFormData({ ...formData, redes_sociais: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      placeholder="Instagram, Facebook, etc."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo da Empresa
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-sky-500 hover:bg-sky-50 transition-colors flex items-center justify-center gap-2 text-gray-600"
                        >
                          <FiUpload size={20} />
                          {logoFile ? logoFile.name : 'Clique para selecionar a logo'}
                        </button>
                        <p className="text-xs text-gray-500 mt-1">
                          Formatos aceitos: JPG, PNG, GIF, WEBP (máx. 5MB)
                        </p>
                      </div>
                      {logoPreview && (
                        <div className="flex-shrink-0">
                          <div className="relative">
                            <img
                              src={logoPreview}
                              alt="Preview da logo"
                              className="h-20 w-20 object-contain border border-gray-300 rounded-lg bg-white p-1"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setLogoFile(null);
                                setLogoPreview(null);
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = '';
                                }
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <FiX size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descrição
                    </label>
                    <textarea
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      placeholder="Descrição sobre a empresa..."
                    />
                  </div>
                </div>

                <div className="flex gap-4 justify-end">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center gap-2"
                  >
                    <FiSave size={18} />
                    {dadosEdit ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DadosEmpresa;
